<!--
  NotificationToast.vue — Phase 2.3: Toast notification stack for WARNING/HIGH level alerts.
  
  Renders a stack of toasts (max 5) anchored top-right.
  Each toast auto-dismisses after 10s with a progress bar countdown.
  Shows agent icon, severity, title, narrative, and dismiss button.
-->
<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'

const AGENTS = {
  centinela:  { label: 'El Centinela', icon: 'mdi-shield-lock-outline', color: 'agent-centinela' },
  jefe:       { label: 'Jefe de Tráfico', icon: 'mdi-truck-fast-outline', color: 'agent-jefe' },
  supervisor: { label: 'El Supervisor', icon: 'mdi-hard-hat', color: 'agent-supervisor' },
  ingeniero:  { label: 'El Ingeniero', icon: 'mdi-office-building-cog-outline', color: 'agent-ingeniero' },
  system:     { label: 'System', icon: 'mdi-cog-outline', color: 'grey' },
}

const SEVERITY_COLORS = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'success',
}

const props = defineProps({
  /** Array of toast notification objects */
  toasts: { type: Array, default: () => [] },
  /** Auto-dismiss timeout in ms */
  timeout: { type: Number, default: 10000 },
})

const emit = defineEmits(['dismiss', 'click'])

// Track progress timers per toast
const timers = ref(new Map())
const progress = ref(new Map())
let frameId = null

function startTimer(id) {
  if (timers.value.has(id)) return
  const startTime = Date.now()
  timers.value.set(id, startTime)
  progress.value.set(id, 100)
}

function stopTimer(id) {
  timers.value.delete(id)
  progress.value.delete(id)
}

// Animation frame loop for smooth progress
function tick() {
  const now = Date.now()
  for (const [id, startTime] of timers.value.entries()) {
    const elapsed = now - startTime
    const pct = Math.max(0, 100 - (elapsed / props.timeout) * 100)
    progress.value.set(id, pct)

    if (pct <= 0) {
      stopTimer(id)
      emit('dismiss', id)
    }
  }
  frameId = requestAnimationFrame(tick)
}

// Start/stop animation loop based on toasts
watch(
  () => props.toasts,
  (toasts) => {
    toasts.forEach(t => startTimer(t.id))

    // Clean up timers for removed toasts
    const ids = new Set(toasts.map(t => t.id))
    for (const id of timers.value.keys()) {
      if (!ids.has(id)) stopTimer(id)
    }

    if (toasts.length > 0 && !frameId) {
      frameId = requestAnimationFrame(tick)
    }
    if (toasts.length === 0 && frameId) {
      cancelAnimationFrame(frameId)
      frameId = null
    }
  },
  { immediate: true, deep: true },
)

onBeforeUnmount(() => {
  if (frameId) cancelAnimationFrame(frameId)
  timers.value.clear()
  progress.value.clear()
})

function getAgent(key) {
  return AGENTS[key] || AGENTS.system
}

function getColor(severity) {
  return SEVERITY_COLORS[severity] || 'info'
}

function getProgress(id) {
  return progress.value.get(id) ?? 100
}

function formatTime(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function handleDismiss(id) {
  stopTimer(id)
  emit('dismiss', id)
}

function handleClick(toast) {
  emit('click', toast)
}
</script>

<template>
  <Teleport to="body">
    <TransitionGroup
      name="toast"
      tag="div"
      class="notification-toast-stack"
    >
      <VCard
        v-for="(toast, i) in toasts"
        :key="toast.id"
        class="notification-toast"
        :style="{ '--toast-index': i }"
        elevation="8"
        @click="handleClick(toast)"
      >
        <!-- Progress bar countdown -->
        <div
          class="toast-progress"
          :class="`bg-${getColor(toast.severity)}`"
          :style="{ width: `${getProgress(toast.id)}%` }"
        />

        <div class="d-flex align-start pa-3 ga-3">
          <!-- Agent icon -->
          <VAvatar :color="getAgent(toast.agent).color" size="36" variant="tonal">
            <VIcon :icon="getAgent(toast.agent).icon" size="18" />
          </VAvatar>

          <!-- Content -->
          <div class="flex-grow-1 min-width-0">
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center ga-2">
                <span class="text-subtitle-2 font-weight-bold text-truncate">
                  {{ toast.title }}
                </span>
                <VChip
                  :color="getColor(toast.severity)"
                  size="x-small"
                  variant="flat"
                  class="text-uppercase"
                >
                  {{ toast.severity }}
                </VChip>
              </div>
              <VBtn
                icon="mdi-close"
                variant="text"
                size="x-small"
                density="compact"
                @click.stop="handleDismiss(toast.id)"
              />
            </div>

            <div
              v-if="toast.narrative"
              class="text-body-2 text-medium-emphasis text-truncate-2 mt-1"
            >
              {{ toast.narrative }}
            </div>

            <div class="d-flex align-center justify-space-between mt-1">
              <span class="text-caption text-disabled">
                {{ getAgent(toast.agent).label }}
              </span>
              <span class="text-caption text-disabled">
                {{ formatTime(toast.timestamp) }}
              </span>
            </div>
          </div>
        </div>
      </VCard>
    </TransitionGroup>
  </Teleport>
</template>

<style scoped>
.notification-toast-stack {
  position: fixed;
  top: 72px;
  right: 16px;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 420px;
  width: 100%;
  pointer-events: none;
}

.notification-toast {
  pointer-events: auto;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border-left: 3px solid rgb(var(--v-theme-warning));
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.notification-toast:hover {
  transform: translateX(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2) !important;
}

.toast-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 3px;
  transition: width 0.1s linear;
  opacity: 0.8;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.min-width-0 {
  min-width: 0;
}

/* Transitions */
.toast-enter-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.toast-leave-active {
  transition: all 0.2s ease-in;
  position: absolute;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
.toast-move {
  transition: transform 0.3s ease;
}
</style>
